import { Location } from '@angular/common';
import { Component, inject, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AppInformationService, AuthService, GenesisUnitOfWork, InvoiceService, UtilityService } from 'app/core/core.module';
import {
    AchTransaction,
    CreditCard,
    Dealer,
    Invoice,
    InvoiceLine,
    InvoicePaymentMethod,
    InvoiceStatus,
    Payment,
    PaymentCredit,
    PaymentCreditType,
    PaymentLine,
} from 'app/core/model/entity-model';
import { Utility } from 'app/core/utility';
import { DxDataGridComponent } from 'devextreme-angular';
import { ValidationCallbackData } from 'devextreme/common';
import { alert, confirm } from 'devextreme/ui/dialog';
import { default as validationEngine } from 'devextreme/ui/validation_engine';
import _ from 'lodash';
import moment from 'moment';
import { BsModalService } from 'ngx-bootstrap/modal';
import { InvoiceDetailComponent } from 'app/invoices/invoice-detail/invoice-detail.component';
import { OrderDetailComponent } from 'app/orders/order-detail/order-detail.component';
import { ApplyCreditDialogComponent } from '../apply-credit-dialog/apply-credit-dialog.component';

@Component({
    selector: 'app-payment-detail',
    templateUrl: './payment-detail.component.html',
    styleUrls: ['./payment-detail.component.scss'],
    standalone: false,
})
export class PaymentDetailComponent implements OnInit {
    invoiceService = inject(InvoiceService);
    utility = inject(UtilityService);
    modalService = inject(BsModalService);

    key: number | string;
    dealerKey: number | string;
    selectedInvoiceKey: number;
    invoicePaymentInfos: any[];
    readOnly: boolean;
    paymentMethods: any[];

    payment: Payment;
    paymentLines: PaymentLine[] = [];
    dealer: Dealer;
    selectedInvoicePaymentInfo: any;
    amountReceived: number;
    balance: number;
    credits: PaymentCredit[];
    creditAmount: number;
    processing: boolean = false;
    canAddNorthPointFee = false;
    creditCards: CreditCard[];
    duplicateReferenceNumberCount: number;
    enteredAmount = 0;
    checkedLineIds: number[] = [];
    achInitiated = false;

    @ViewChild('dataGrid') dataGrid: DxDataGridComponent;
    @ViewChild('creditsGrid') creditsGrid: DxDataGridComponent;

    constructor(
        private uow: GenesisUnitOfWork,
        private route: ActivatedRoute,
        private router: Router,
        private location: Location,
        private appInfo: AppInformationService,
        private auth: AuthService,
    ) {}

    canDeactivate() {
        console.log('canDeactivate', this.hasChanges(), this.uow.entityChanged);
        return !this.hasChanges();
    }

    async afterGuardRuns() {
        console.log('afterGuardRuns');
        await alert('Changes have been discarded.', this.appInfo.settings.company);
        this.uow.rollback();
    }

    async ngOnInit() {
        this.paymentMethods = await this.uow.invoicePaymentMethods.whereActive();
        this.creditCards = (await this.uow.creditCards.whereActive('displayOrder')) as CreditCard[];

        // need to subscribe to refresh page when navigating to related payments
        this.route.params.subscribe(async (params: any) => {
            this.key = params.id;
            this.dealerKey = Number(params.dealerId);
            this.selectedInvoiceKey = Number(params.selectedInvoiceId);

            if (this.key === 'new') {
                await this.getDealer(this.dealerKey);
                await this.getCredits(this.dealerKey);

                this.payment = this.uow.payments.createEntity({
                    dealerId: this.dealerKey,
                    created: new Date(),
                    paymentDate: new Date(),
                    createdBy: this.auth.currentUser.userName,
                }) as Payment;

                await this.getInvoiceInfos();
                this.createOpenInvoiceLines();
                this.setInvoiceAmounts();
                this.getSelectedInvoice(this.selectedInvoiceKey);
                this.balance = _.sum(
                    this.paymentLines.map((x) => {
                        return x.startingBalance;
                    }),
                );
                this.applyDefaultSelectedInvoice();
            } else if (this.key === 'creditPayment') {
                await this.getDealer(this.dealerKey);
                const invoice = (await this.uow.invoices.byId(this.selectedInvoiceKey)) as Invoice;
                this.payment = this.uow.payments.createEntity({
                    dealerId: this.dealerKey,
                    created: new Date(),
                    paymentDate: new Date(),
                    createdBy: this.auth.currentUser.userName,
                    notes: `Payment created from Credit Payment on Invoice# ${invoice.invoiceNumber}`,
                }) as Payment;

                console.log('invoice', invoice.invoiceDate);
                const paymentLine = this.uow.paymentLines.createEntity({
                    paymentId: this.payment.id,
                    invoiceId: this.selectedInvoiceKey,
                    lineNumber: Payment.getNextLineNumber(this.paymentLines),
                    description: `Invoice ${invoice.invoiceNumber}`,
                    amount: 0,
                    created: new Date(),
                    createdBy: this.auth.currentUser.userName,
                    generatedDescription: `Invoice ${invoice.invoiceNumber}`,
                }) as PaymentLine;
                paymentLine.invoiceDate = invoice.invoiceDate;
                paymentLine.invoiceDueDate = moment(invoice.invoiceDate).add(30, 'days').toDate();
                this.paymentLines.push(paymentLine);
                return;
            } else {
                this.readOnly = true;
                await this.getData();
            }
            await this.getDuplicateReferenceNumberCount();
            this.updateAmountReceived();
            this.dataGrid.instance.refresh();
        });
    }

    async getData() {
        this.payment = (await this.uow.payments.byId(
            this.key,
            'dealer, paymentLines.paymentCredit.creditOrder.orderLines.returnedDetail.invoice, paymentLines.invoice.achTransactions',
        )) as Payment;
        this.dealer = this.payment.dealer;
        this.paymentLines = this.payment.paymentLines;
        this.paymentLines.forEach((line: any) => {
            if (line.paymentCredit) line.paymentCredit.creditOrderInvoices = this.getCreditOrderInvoices(line.paymentCredit);
        });
        await this.getInvoiceInfos();
        this.setInvoiceAmounts();
        this.updateAchInitiated();
        this.dataGrid.instance.refresh();
    }

    updateAchInitiated() {
        this.achInitiated = this.paymentLines.some(
            (line: any) => !line.deleted && line.invoice?.achTransactions?.some((tx: AchTransaction) => !tx.deleted && tx.transferInitiated && !tx.depositAcknowledged),
        );
    }

    async checkOpenAchOnPaymentReceived() {
        const paidInvoiceIds = _.uniq(
            this.paymentLines
                .filter((line: any) => !line.deleted && line.invoiceId && Utility.round(line.amount + line.creditAmount, 2) !== 0)
                .map((line: any) => line.invoiceId),
        );
        if (paidInvoiceIds.length === 0) return;

        const openTransactionsByInvoice = await Promise.all(paidInvoiceIds.map((invoiceId) => this.uow.getAchTransactionsByInvoiceId(invoiceId, 'ach.achTransactions')));
        const openTransactions = _.flatten(openTransactionsByInvoice).filter((tx: AchTransaction) => !tx.depositAcknowledged);
        if (openTransactions.length === 0) return;

        if (paidInvoiceIds.length === 1) {
            const invoiceId = paidInvoiceIds[0];
            const paidAmount = Utility.round(
                _.sumBy(
                    this.paymentLines.filter((line: any) => !line.deleted && line.invoiceId === invoiceId),
                    (line: any) => line.amount + line.creditAmount,
                ),
                2,
            );
            const openAmount = Utility.round(_.sumBy(openTransactions, 'amount'), 2);

            // If the payment covers a single invoice and matches the open ACH amount exactly, remove it
            if (paidAmount === openAmount) {
                const deletedBy = `${this.auth.currentUser.userName} - removed: payment received matching open ACH amount`;
                openTransactions.forEach((tx: any) => {
                    tx.deleted = new Date();
                    tx.deletedBy = deletedBy;
                });
                // Remove the ACH itself if it has no remaining transactions
                _.uniqBy(
                    openTransactions.map((tx: AchTransaction) => tx.ach).filter((ach) => !!ach),
                    'id',
                ).forEach((ach) => {
                    if (ach.achTransactions.every((tx) => !!tx.deleted)) {
                        ach.deleted = new Date();
                        ach.deletedBy = deletedBy;
                    }
                });
                await this.uow.commit();
                await this.utility.sendOpenAchPaymentReceivedAlert(this.payment.id, true);
                return;
            }
        }

        await this.utility.sendOpenAchPaymentReceivedAlert(this.payment.id, false);
    }

    async getDealer(id: number) {
        this.dealer = (await this.uow.dealers.byId(id)) as Dealer;
    }
    getSelectedInvoice(id: number) {
        this.selectedInvoicePaymentInfo = this.invoicePaymentInfos.find((invoice) => invoice.id == id);
    }
    getCreditOrderInvoices(credit: any): any[] {
        if (!credit.creditOrderId || !credit.creditOrder) return [];

        const invoices = [];
        const seenInvoiceIds = new Set<number>();
        credit.creditOrder.orderLines.forEach((orderLine) => {
            const detail = orderLine.returnedDetail;
            if (detail && !detail.deleted && detail.invoiceId && detail.invoice && !seenInvoiceIds.has(detail.invoiceId)) {
                seenInvoiceIds.add(detail.invoiceId);
                invoices.push({ id: detail.invoice.id, invoiceNumber: detail.invoice.invoiceNumber });
            }
        });
        return invoices;
    }
    async getCredits(dealerId: number) {
        const credits = await this.uow.getPaymentCreditsForDealerId(dealerId, 'creditOrder.orderLines.returnedDetail.invoice');
        credits.forEach((credit) => {
            credit.balance = credit.availableAmount;
            credit.creditOrderInvoices = this.getCreditOrderInvoices(credit);
        });
        this.credits = credits;
    }
    async getInvoiceInfos() {
        if (this.key === 'new') {
            this.invoicePaymentInfos = await this.uow.getOpenInvoicePaymentInfos(this.dealer.id);
        } else {
            this.invoicePaymentInfos = await this.uow.getInvoicePaymentInfos(this.payment.id);
        }
    }

    async getDuplicateReferenceNumberCount() {
        this.duplicateReferenceNumberCount = await this.uow.getDuplicateReferenceNumberCount(this.dealer.id, moment(new Date()).format('YYYYMMDD'));
    }

    setInvoiceAmounts() {
        this.paymentLines.forEach((paymentLine) => {
            const paymentInfo = this.invoicePaymentInfos.find((invoice) => invoice.id === paymentLine.invoiceId);
            if (!!paymentInfo && !!paymentInfo.description) {
                paymentLine.generatedDescription = paymentInfo.description;
            } else {
                paymentLine.generatedDescription = paymentLine.description;
            }
            if (!paymentInfo) return;

            paymentLine.invoiceDate = paymentInfo.invoice.invoiceDate
                ? moment(paymentInfo.invoice.invoiceDate).toDate()
                : paymentInfo.invoice.created
                  ? moment(paymentInfo.invoice.created).toDate()
                  : null;
            paymentLine.invoiceDueDate = paymentInfo.dueDate
                ? moment(paymentInfo.dueDate).toDate()
                : paymentInfo.invoice.created
                  ? moment(paymentInfo.invoice.created).add(30, 'days').toDate()
                  : null;
            paymentLine.invoiceAmount = paymentInfo.total;
            paymentLine.paidAmount = paymentInfo.paidAmount;
            paymentLine.startingBalance = paymentInfo.remainingBalance;
            this.updateLineRemainingBalance(paymentLine.id);
            paymentLine.relatedPaymentIds = paymentInfo.relatedPaymentIds;
        });
    }

    createOpenInvoiceLines() {
        this.invoicePaymentInfos.forEach((invoice) => {
            const paymentLine = this.uow.paymentLines.createEntity({
                paymentId: this.payment.id,
                invoiceId: invoice.id,
                lineNumber: Payment.getNextLineNumber(this.paymentLines),
                description: `Invoice ${invoice.invoiceNumber}`,
                amount: 0,
                created: new Date(),
                createdBy: this.auth.currentUser.userName,
            }) as PaymentLine;
            this.paymentLines.push(paymentLine);
        });
    }

    applyDefaultSelectedInvoice() {
        this.enteredAmount = this.selectedInvoicePaymentInfo ? this.selectedInvoicePaymentInfo.remainingBalance : 0;

        const defaultLine = this.paymentLines.find((line) => line.invoiceId === this.selectedInvoiceKey) as any;
        if (defaultLine) {
            defaultLine.applyPayment = true;
            this.checkedLineIds = [defaultLine.id];
        }
        this.redistributeAmounts();
    }

    creditCheck(e) {}

    get isCreditMemoMethod(): boolean {
        return (this.payment?.paymentMethod?.name ?? '').trim().toUpperCase() === 'CREDIT MEMO';
    }

    onMethodUpdate(e?) {
        if (!this.payment.quickBooksId) {
            this.canAddNorthPointFee = this.payment.paymentMethod.isFloorplanMethod;
            if (this.payment.paymentMethodId === InvoicePaymentMethod.Methods.Cash || this.payment.paymentMethodId === InvoicePaymentMethod.Methods.CreditCard) {
                this.payment.referenceNumber = moment(new Date()).format('YYYYMMDD');
                if (this.duplicateReferenceNumberCount > 0) {
                    this.payment.referenceNumber = this.payment.referenceNumber + '-' + this.duplicateReferenceNumberCount;
                }
            }
            if (this.payment.paymentMethodId === InvoicePaymentMethod.Methods.ACH) {
                this.achCheck();
            }
            if (!this.payment.paymentMethod.isFloorplanMethod) {
                this.payment.northpointFee = 0;
            }
            if (this.payment.paymentMethodId !== InvoicePaymentMethod.Methods.CreditCard) {
                this.payment.creditCardId = null;
                this.payment.creditCardLastDigits = null;
            }
            if (this.isCreditMemoMethod) {
                this.lockAmountsForCreditMemo();
            }
        }
        this.updateAmountReceived();
    }

    lockAmountsForCreditMemo() {
        this.enteredAmount = 0;
        this.checkedLineIds = [];
        this.paymentLines.forEach((line: any) => {
            line.applyPayment = false;
            line.amount = 0;
            this.updateLineRemainingBalance(line.id);
        });
    }

    async achCheck() {
        this.appInfo.pleaseWaitShow();
        const detailIds = this.paymentLines.map((x) => {
            if (x.orderLineDetailId > 0) return x.orderLineDetailId;
        });
        const result = await this.utility.paymentACHCheck(detailIds);
        this.appInfo.pleaseWaitHide();
        if (result) {
            await alert(`Some of the details on this payment have not had ACH Pricing Applied`, `${this.appInfo.settings.company}`);
        }
    }

    onCreditsToolbarPreparing(e) {
        e.toolbarOptions.items.unshift({
            location: 'after',
            widget: 'dxButton',
            options: {
                icon: 'refresh',
                elementAttr: { title: 'Reset Grid Layout' },
                onClick: () => {
                    e.component.state({});
                },
            },
        });
    }

    onToolbarPreparing(e) {
        e.toolbarOptions.items.unshift(
            {
                location: 'before',
                template: 'toolbarButtons',
            },
            {
                location: 'after',
                template: 'toolbarText',
            },
            {
                location: 'after',
                widget: 'dxButton',
                options: {
                    icon: 'revert',
                    elementAttr: { title: 'Reset Filters' },
                    onClick: () => {
                        e.component.clearFilter();
                    },
                },
            },
            {
                location: 'after',
                widget: 'dxButton',
                options: {
                    icon: 'refresh',
                    elementAttr: { title: 'Reset Grid Layout' },
                    onClick: () => {
                        e.component.state({});
                    },
                },
            },
        );
    }
    customizeColumns = (columns: any[]) => {
        columns.forEach((column) => {
            if (column.dataField !== 'amount' && column.dataField !== 'notes') {
                column.allowEditing = false;
            }

            if (column.dataField === 'amount') {
                column.allowEditing = this.key === 'new' || this.key === 'creditPayment';
            }
        });
    };
    onEditingStart(e) {
        if (e.column?.dataField === 'amount' && e.data.applyPayment) {
            e.cancel = true;
        }
    }
    onCellPrepared(e) {
        if (e.rowType === 'data' && e.column?.dataField === 'amount' && e.data.applyPayment) {
            e.cellElement.classList.add('locked-amount-cell');
        }
    }
    rowPrepare(e) {
        if (e.rowType === 'data') {
            const amount = e.data.amount + e.data.creditAmount;
            if (amount > 0) {
                if (this.key === 'new') {
                    if (amount < e.data.startingBalance) e.rowElement.className = e.rowElement.className + ' partial-payment';
                    else if (amount > e.data.startingBalance) e.rowElement.className = e.rowElement.className + ' over-payment';
                    else e.rowElement.className = e.rowElement.className + ' full-payment';
                } else {
                    if (e.data.startingBalance !== 0) e.rowElement.className = e.rowElement.className + ' partial-payment';
                    else e.rowElement.className = e.rowElement.className + ' full-payment';
                }
            }
        }
    }

    creditRowPrepare(e) {
        if (e.rowType === 'data') {
            if (e.data.amount > 0) {
                if (this.key === 'new') {
                    if (e.data.amount < e.data.startingBalance) e.rowElement.className = e.rowElement.className + ' partial-payment';
                    else if (e.data.amount > e.data.startingBalance) e.rowElement.className = e.rowElement.className + ' over-payment';
                    else e.rowElement.className = e.rowElement.className + ' full-payment';
                } else {
                    if (e.data.startingBalance !== 0) e.rowElement.className = e.rowElement.className + ' partial-payment';
                    else e.rowElement.className = e.rowElement.className + ' full-payment';
                }
            }
        }
    }

    rowUpdated(e) {
        if (this.readOnly) return;

        this.updateLineRemainingBalance(e.data.id);
        this.updateAmountReceived();
    }

    onApplyToggle(line: any, e: any) {
        if (this.readOnly || this.isCreditMemoMethod) return;

        line.applyPayment = e.value;
        if (e.value) {
            if (!this.checkedLineIds.includes(line.id)) this.checkedLineIds.push(line.id);
        } else {
            this.checkedLineIds = this.checkedLineIds.filter((id) => id !== line.id);
            line.amount = 0;
            this.updateLineRemainingBalance(line.id);
        }

        this.redistributeAmounts();
        this.updateAmountReceived();
        this.dataGrid.instance.refresh();
    }

    onEnteredAmountChanged(e?) {
        this.redistributeAmounts();
        this.updateAmountReceived();
        this.dataGrid.instance.refresh();
    }

    redistributeAmounts() {
        let remaining = Utility.round(this.enteredAmount - this.payment.northpointFee, 2);
        this.checkedLineIds.forEach((lineId) => {
            const line = this.paymentLines.find((x) => x.id === lineId) as any;
            if (!line || line.deleted) return;

            const lineCap = Math.max(line.startingBalance - (line.creditAmount || 0), 0);
            if (remaining <= 0 || lineCap <= 0) {
                line.amount = 0;
            } else {
                const amount = Utility.round(Math.min(lineCap, remaining), 2);
                line.amount = amount;
                remaining -= amount;
            }
            this.updateLineRemainingBalance(line.id);
        });
    }

    updateLineRemainingBalance(lineId: number) {
        const line = this.paymentLines.find((line) => line.id === lineId) as any;
        line.remainingBalance = this.key === 'new' ? line.startingBalance - line.amount - (line.creditAmount || 0) : line.startingBalance;
    }

    updateAmountReceived() {
        this.amountReceived =
            _.sum(
                this.paymentLines
                    .filter((x) => !x.deleted)
                    .map((paymentLine) => {
                        return paymentLine.amount + paymentLine.creditAmount;
                    }),
            ) + this.payment.northpointFee;
        this.creditAmount = _.sum(
            this.paymentLines
                .filter((x) => !x.deleted)
                .map((paymentLine) => {
                    return paymentLine.creditAmount;
                }),
        );
    }

    async validateLines(): Promise<boolean> {
        if (
            this.paymentLines.filter((x) => {
                if (x.amount < 0) return x;
            }).length > 0
        ) {
            await alert('Payment Lines CANNOT be negative.', this.appInfo.settings.company);
            return false;
        }

        if (this.paymentLines.some((x) => x.creditAmount > 0 && !x.paymentCreditId)) {
            await alert(
                'Payment Lines with Credit Amounts must have an associated CreditId.  Clear the Credit Amount and apply the credit from the RH Grid',
                this.appInfo.settings.company,
            );
            return false;
        }

        if (this.paymentLines.every((x) => x.amount === 0 && x.creditAmount === 0)) {
            await alert('No payment applied. All payment lines are zero.', this.appInfo.settings.company);
            return false;
        }

        //check the total creditAmount and update the associated credits available amount
        const groupedPaymentLines = _.groupBy(this.paymentLines, 'paymentCreditId');
        let creditCheck = true;
        for (const creditId of Object.keys(groupedPaymentLines)) {
            if (creditId !== 'null') {
                const credit = this.credits.find((x) => x.id === Number(creditId));
                if (credit) {
                    const totalCreditAmount = _.sumBy(groupedPaymentLines[creditId], 'creditAmount');
                    if (totalCreditAmount > credit.availableAmount) {
                        creditCheck = false;
                        break;
                    }
                }
            }
        }
        if (!creditCheck) {
            await alert(`The total credit amount exceeds the available balance.`, this.appInfo.settings.company);
            return false;
        }

        if ((this.key === 'new' || this.key === 'creditPayment') && !this.isCreditMemoMethod) {
            const appliedAmount = Utility.round(
                _.sumBy(
                    this.paymentLines.filter((x) => !x.deleted),
                    'amount',
                ) + this.payment.northpointFee,
                2,
            );
            if (Utility.round(this.enteredAmount, 2) !== appliedAmount) {
                await alert(
                    `Payment Amount entered ($${this.enteredAmount.toFixed(2)}) does not match the amount applied to invoices ($${appliedAmount.toFixed(2)}).`,
                    this.appInfo.settings.company,
                );
                return false;
            }
        }

        return true;
    }

    async save(andExport?: boolean) {
        if (!this.valid()) {
            await alert('Please fix errors before saving', this.appInfo.settings.company);
            return;
        }
        const validLines = await this.validateLines();
        if (!validLines) {
            return;
        }

        if (this.key === 'new' || this.key === 'creditPayment') {
            const startingBalanceSum = _.sum(
                this.paymentLines.map((x: any) => {
                    if (x.amount > 0 || x.creditAmount > 0) return x.startingBalance;
                }),
            );
            if (startingBalanceSum !== this.amountReceived && this.amountReceived > startingBalanceSum) {
                const creditConfirm = await confirm(
                    `A payment credit will be generated for $${Utility.round(this.amountReceived - startingBalanceSum, 2).toFixed(2)}.  Continue?`,
                    this.appInfo.settings.company,
                );
                if (!creditConfirm) return;
            }

            this.appInfo.pleaseWaitShow();
            this.paymentLines.forEach((line: any) => {
                if (!line.amount && !line.creditAmount) {
                    line.entityAspect.setDeleted();
                } else {
                    if (this.key === 'new') {
                        console.log('line', this.invoicePaymentInfos);
                        const openInvoice = this.invoicePaymentInfos.find((invoice) => invoice.id === line.invoiceId);
                        if (!openInvoice) return;

                        const lineAmount = line.amount + this.payment.northpointFee + line.creditAmount;

                        if (lineAmount > line.startingBalance) {
                            line.overPaymentAmount = Utility.round(line.amount - line.startingBalance, 2);
                            line.amount = line.startingBalance;
                        }
                        if (lineAmount >= line.startingBalance) {
                            openInvoice.invoice.paid = new Date();
                            openInvoice.invoice.statusId = InvoiceStatus.Statuses.Paid;
                        }
                        if (lineAmount < line.startingBalance) {
                            openInvoice.invoice.statusId = InvoiceStatus.Statuses.PartiallyPaid;
                        }
                    } else if (this.key === 'creditPayment') {
                        const lineAmount = line.amount + this.payment.northpointFee + line.creditAmount;

                        if (lineAmount > line.startingBalance) {
                            line.overPaymentAmount = Utility.round(line.amount - line.startingBalance, 2);
                            line.amount = line.startingBalance;
                        }
                    }
                }
            });

            //update the credits available amount
            const groupedPaymentLines = _.groupBy(this.paymentLines, 'paymentCreditId');
            Object.keys(groupedPaymentLines).forEach((creditId) => {
                if (creditId !== 'null') {
                    const credit = this.credits.find((x) => x.id === Number(creditId));
                    if (credit) {
                        const totalCreditAmount = _.sumBy(groupedPaymentLines[creditId], 'creditAmount');
                        credit.availableAmount -= totalCreditAmount;
                    }
                }
            });

            //consolidate the credits to a single credit
            const creditAmount = Utility.round(
                _.sum(
                    this.paymentLines.map((x: any) => {
                        return x.overPaymentAmount;
                    }),
                ),
                2,
            );
            if (creditAmount > 0) {
                this.uow.paymentCredits.createEntity({
                    dealerId: this.dealer.id,
                    paymentCreditTypeId: PaymentCreditType.Types.OverPayment,
                    fromPaymentId: this.payment.id,
                    creditAmount: creditAmount,
                    availableAmount: creditAmount,
                    created: new Date(),
                    createdBy: this.auth.currentUser.userName,
                });
                //creates extra payment line for single point of deductions when applying credits later
                const paymentLine = this.uow.paymentLines.createEntity({
                    paymentId: this.payment.id,
                    // invoiceId: this.selectedInvoicePaymentInfo.id,
                    lineNumber: Payment.getNextLineNumber(this.paymentLines),
                    description: `OverPayment Record`,
                    amount: creditAmount,
                    created: new Date(),
                    createdBy: this.auth.currentUser.userName,
                }) as PaymentLine;
                this.paymentLines.push(paymentLine);
            }

            await this.uow.commit();
            const invoiceIds = this.paymentLines.filter((paymentLine) => !paymentLine.entityAspect.entityState.isDetached()).map((paymentLine) => paymentLine.invoiceId);
            // await this.invoiceService.updateBalances(invoiceIds);
            await this.checkOpenAchOnPaymentReceived();
            this.key = this.payment.id;
            if (andExport) {
                this.payment.readyToExport = new Date();
                await this.uow.commit();
            }
            this.appInfo.pleaseWaitHide();
            await this.router.navigate([`/payments/${this.payment.id}`], { relativeTo: this.route, replaceUrl: true });
        }

        this.payment.total = this.amountReceived;
        await this.uow.commit();
        return;
    }

    async cancel() {
        if (!(await confirm('Are you sure you want to cancel changes?', this.appInfo.settings.company))) return;
        this.uow.rollback();
        if (this.key !== 'creditPayment') this.setInvoiceAmounts(); // if a line was edited, rolling back clears invoice properties on the line
        if (!this.key || this.key === 'new' || this.key === 'creditPayment') this.close();
    }

    close() {
        try {
            this.location.back();
        } catch {
            this.router.navigate(['../'], { relativeTo: this.route });
        }
    }

    hasChanges() {
        return this.uow.hasChanges();
    }

    valid() {
        const groupConfig = validationEngine.getGroupConfig('invoiceValidationGroup');
        if (groupConfig) {
            const result = validationEngine.validateGroup('invoiceValidationGroup');
            return result.isValid;
        } else return false;
    }

    validateLastDigits = (e: ValidationCallbackData) => {
        const lastDigits = e.value;
        if (!lastDigits) return true;
        const value = Number(lastDigits);
        if (isNaN(value)) return false;
        // returns false if string includes decimal, 'e', the '+' or '-' sign, etc.
        const onlyContainsDigits = /^\d+$/.test(lastDigits);
        if (!onlyContainsDigits) return false;
        return lastDigits.length === 4 && value >= 0 && value <= 9999;
    };

    updateDisplayOrder(invoiceLines: InvoiceLine[]) {
        _.orderBy(this.paymentLines, 'lineNumber').forEach((invoiceLine, index) => {
            if (invoiceLine.lineNumber !== index + 1) invoiceLine.lineNumber = index + 1;
        });
    }

    sort(invoiceLines: InvoiceLine[]) {
        invoiceLines.sort((a, b) => (a.lineNumber || 9999) - (b.lineNumber || 9999));
    }

    printPaymentReceipt() {
        // this.reportService.printPaymentReceiptReport([this.payment.id]);
    }

    async openPayment(paymentId) {
        this.router.navigate([`/payments/${paymentId}`]);
    }

    openInvoiceDialog(invoiceId) {
        if (!invoiceId) return;

        const modalRef = this.modalService.show(InvoiceDetailComponent, {
            class: 'modal-lg',
            ignoreBackdropClick: true,
            keyboard: false,
            initialState: {
                showAsDialog: true,
                invoiceId,
            },
        });

        modalRef.content.result.subscribe(() => {
            modalRef.hide();
        });
    }

    openOrderDialog(orderId) {
        if (!orderId) return;

        const modalRef = this.modalService.show(OrderDetailComponent, {
            class: 'modal-lg',
            ignoreBackdropClick: true,
            keyboard: false,
            initialState: {
                showAsDialog: true,
                orderId,
            },
        });

        modalRef.content.result.subscribe(() => {
            modalRef.hide();
        });
    }

    async readyToExport() {
        this.invoiceService.paymentsReadyToExport([this.payment]);
        await this.getData();
    }

    async markOldInvoiceAsPaid() {
        const reult = await confirm('Are you sure you mark the selected rows as Previously Paid Invoices?', this.appInfo.settings.company);
        if (!reult) return;

        const selectedRows = this.dataGrid.instance.getSelectedRowsData();
        this.appInfo.pleaseWaitShow();
        for (let i = 0; i < selectedRows.length; i++) {
            const row = selectedRows[i];
            const paymentLine = this.paymentLines.find((line) => line.id === row.id);
            const invoice = (await this.uow.invoices.byId(paymentLine.invoiceId)) as Invoice;
            if (invoice) {
                invoice.statusId = InvoiceStatus.Statuses.Paid;
                invoice.paid = new Date();
                invoice.quickBooksId = `Marked as Paid`;
                invoice.quickBooksLastExport = new Date();
                invoice.readyToExport = new Date();
                await this.uow.commit([invoice]);
            }
            const index = this.paymentLines.findIndex((line) => line.id === row.id);
            if (index !== -1) {
                this.paymentLines.splice(index, 1);
            }
        }
        this.appInfo.pleaseWaitHide();
    }

    async delete() {
        const hasCredit = this.paymentLines.some((line) => !!line.paymentCreditId);
        if (hasCredit) {
            alert('Payment Credit needs to be deleted before this payment can be deleted', 'Delete Payment');
            return;
        }

        const result = await confirm('Are you sure you want to delete this payment?', this.appInfo.settings.company);
        if (!result) return;

        if (this.payment.quickBooksId) {
            const qbResult = await confirm(
                'This payment has already been exported to QuickBooks.  If you delete it, it will need to be manually deleted from QuickBooks. Continue?',
                this.appInfo.settings.company,
            );
            if (!qbResult) return;
        }

        this.appInfo.pleaseWaitShow();
        this.paymentLines.forEach((line) => {
            line.deleted = new Date();
            line.deletedBy = this.auth.currentUser.userName;
            if (line.invoiceId) {
                line.invoice.paid = null;
                if (line.amount >= line.invoice.total) line.invoice.statusId = InvoiceStatus.Statuses.New;
                else line.invoice.statusId = InvoiceStatus.Statuses.PartiallyPaid;
            }
            if (!!line.paymentCreditId) {
                line.paymentCredit.availableAmount += line.creditAmount;
                line.paymentCredit.applied = null;
                line.paymentCredit = null;
                line.creditAmount = 0;
            }
        });
        this.payment.deleted = new Date();
        this.payment.deletedBy = this.auth.currentUser.userName;
        await this.uow.commit();
        this.appInfo.pleaseWaitHide();
        this.close();
    }

    openApplyCreditDialog() {
        const eligibleLines = this.paymentLines.filter(
            (line) => !line.deleted && !line.paymentCreditId && Utility.round(line.startingBalance - (line.creditAmount || 0) - (line.amount || 0), 2) > 0,
        );
        const availableCredits = this.credits.filter((credit) => credit.balance > 0);

        const selectedRows = this.dataGrid.instance.getSelectedRowsData();
        const preselectedLine = selectedRows.length === 1 ? eligibleLines.find((line) => line.id === selectedRows[0].id) : undefined;

        const selectedCreditRows = this.creditsGrid.instance.getSelectedRowsData();
        const preselectedCredit = selectedCreditRows.length === 1 ? availableCredits.find((credit) => credit.id === selectedCreditRows[0].id) : undefined;

        const modalRef = this.modalService.show(ApplyCreditDialogComponent, {
            class: 'modal-md',
            ignoreBackdropClick: true,
            keyboard: false,
            initialState: {
                credits: availableCredits,
                paymentLines: eligibleLines,
                paymentLineId: preselectedLine?.id,
                creditId: preselectedCredit?.id,
            },
        });

        modalRef.content.result.subscribe((result: any) => {
            if (result.success) {
                this.paymentLines.forEach((line) => this.updateLineRemainingBalance(line.id));
                this.redistributeAmounts();
                this.updateAmountReceived();
                this.dataGrid.instance.refresh();
                this.creditsGrid.instance.refresh();
            }
            modalRef.hide();
        });
    }

    canRemoveCredit(): boolean {
        const selectedRows = this.dataGrid?.instance.getSelectedRowsData() ?? [];
        return selectedRows.length === 1 && selectedRows[0].creditAmount > 0;
    }

    async removeCredit() {
        const selectedRows = this.dataGrid.instance.getSelectedRowsData();
        const paymentLine = selectedRows[0] as any;
        const credit = (await this.uow.paymentCredits.byId(paymentLine.paymentCreditId)) as any;
        if (credit) {
            credit.balance = Utility.round(Math.min(credit.balance + paymentLine.creditAmount, credit.availableAmount), 2);
        }
        paymentLine.creditAmount = 0;
        paymentLine.paymentCreditId = null;
        this.updateLineRemainingBalance(paymentLine.id);
        this.updateAmountReceived();
        this.dataGrid.instance.refresh();
        this.creditsGrid.instance.refresh();
    }
}
